import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Conductor, ConductorStateTypes } from "./Conductor.util";

const name = "conductors";
const extraActions = createExtraActions();

const ConductorInitialState: ConductorStateTypes = {
  createConductor: ResetApiState(null),
  resetCreateConductor: ResetApiState(null),
  editConductor: ResetApiState(null),
  resetEditConductor: ResetApiState(null),
  deleteConductor: ResetApiState(null),
  resetDeleteConductor: ResetApiState(null),
  conductors: ResetApiState([]),
  pagedConductors: ResetApiState(ResetPaginationState()),
  conductor: ResetApiState(null),
  resetConductor: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createConductor = createAsyncThunk<ResponseWrapper<Conductor>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateConductor = createAction(`/reset-create-${name}`);

  const editConductor = createAsyncThunk<ResponseWrapper<Conductor>, any>(
    `/edit-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .put(`/${name}/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditConductor = createAction(`/reset-edit-${name}`);

  const deleteConductor = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteConductor = createAction(`/reset-delete-${name}`);

  const getConductors = createAsyncThunk<Conductor[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedConductors = createAsyncThunk<PaginationType<Conductor[]>, any>(
    `/get-paged-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/paged${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getConductor = createAsyncThunk<Conductor, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetConductor = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createConductor,
    resetCreateConductor,
    editConductor,
    resetEditConductor,
    deleteConductor,
    resetDeleteConductor,
    getConductors,
    getPagedConductors,
    getConductor,
    resetConductor,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<ConductorStateTypes>
) {
  return {
    ...createConductorReducer(),
    ...resetCreateConductor(),
    ...editConductorReducer(),
    ...resetEditConductor(),
    ...deleteConductorReducer(),
    ...resetDeleteConductor(),
    ...conductorsReducer(),
    ...pagedConductorsReducer(),
    ...conductorReducer(),
    ...resetConductor(),
    ...resetStatesReducer(),
  };

  function createConductorReducer() {
    return {
      ...builder.addCase(
        extraActions.createConductor.pending,
        (state, action) => {
          state.createConductor = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createConductor.fulfilled,
        (state, action) => {
          state.createConductor = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createConductor.rejected,
        (state, action) => {
          state.createConductor = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateConductor() {
    return {
      ...builder.addCase(extraActions.resetCreateConductor, (state, action) => {
        state.resetCreateConductor = ResetApiState(true);
        state.createConductor = ResetApiState(null);
      }),
    };
  }

  function editConductorReducer() {
    return {
      ...builder.addCase(
        extraActions.editConductor.pending,
        (state, action) => {
          state.editConductor = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editConductor.fulfilled,
        (state, action) => {
          state.editConductor = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editConductor.rejected,
        (state, action) => {
          state.editConductor = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetEditConductor() {
    return {
      ...builder.addCase(extraActions.resetEditConductor, (state, action) => {
        state.resetEditConductor = ResetApiState(true);
        state.editConductor = ResetApiState(null);
      }),
    };
  }

  function deleteConductorReducer() {
    return {
      ...builder.addCase(
        extraActions.deleteConductor.pending,
        (state, action) => {
          state.deleteConductor = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteConductor.fulfilled,
        (state, action) => {
          state.deleteConductor = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteConductor.rejected,
        (state, action) => {
          state.deleteConductor = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteConductor() {
    return {
      ...builder.addCase(extraActions.resetDeleteConductor, (state, action) => {
        state.resetDeleteConductor = ResetApiState(true);
        state.deleteConductor = ResetApiState(null);
      }),
    };
  }

  function conductorsReducer() {
    return {
      ...builder.addCase(
        extraActions.getConductors.pending,
        (state, action) => {
          state.conductors = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductors.fulfilled,
        (state, action) => {
          state.conductors = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductors.rejected,
        (state, action) => {
          state.conductors = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function pagedConductorsReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedConductors.pending,
        (state, action) => {
          state.pagedConductors = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedConductors.fulfilled,
        (state, action) => {
          state.pagedConductors = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedConductors.rejected,
        (state, action) => {
          state.pagedConductors = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function conductorReducer() {
    return {
      ...builder.addCase(extraActions.getConductor.pending, (state, action) => {
        state.conductor = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getConductor.fulfilled,
        (state, action) => {
          state.conductor = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getConductor.rejected,
        (state, action) => {
          state.conductor = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetConductor() {
    return {
      ...builder.addCase(extraActions.resetConductor, (state, action) => {
        state.resetConductor = ResetApiState(true);
        state.conductor = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createConductor = ResetApiState(null);
        state.resetCreateConductor = ResetApiState(null);
        state.editConductor = ResetApiState(null);
        state.resetEditConductor = ResetApiState(null);
        state.deleteConductor = ResetApiState(null);
        state.resetDeleteConductor = ResetApiState(null);
        state.conductors = ResetApiState([]);
        state.conductor = ResetApiState(null);
        state.resetConductor = ResetApiState(null);
        state.resetConductor = ResetApiState(null);
      }),
    };
  }
}

const ConductorSlice = createSlice({
  name,
  initialState: ConductorInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const ConductorActions = {
  ...ConductorSlice.actions,
  ...extraActions,
};
export const ConductorReducer = ConductorSlice.reducer;
